import React from 'react';
import { ArrowRight, BarChart3, TrendingUp, DollarSign } from 'lucide-react';
import FeatureCard from '../ui/FeatureCard';

const InvestmentOverview = () => {
  return (
    <div className="space-y-8">
      <div className="bg-white p-8 rounded-xl shadow-md">
        <h3 className="text-2xl font-bold text-primary mb-4">Current Fundraising Round</h3>
        <div className="flex flex-col md:flex-row items-center md:items-start mb-6">
          <div className="md:w-2/3 mb-6 md:mb-0 md:pr-8">
            <p className="text-gray-700 mb-4">
              Senti is currently raising £10 million to advance our pioneering Large Biosignal Model (LBM) 
              technology, accelerate regulatory approval, and scale market adoption through strategic partnerships.
            </p>
            <p className="text-gray-700 mb-4">
              This round will fund key milestones over the next two years, positioning Senti at the forefront of
              a healthcare revolution and establishing our technology as the industry standard for AI-driven diagnostics.
            </p>
            <div className="mt-6">
              <a href="/contact#contact" className="bg-primary text-white px-8 py-3 rounded-md font-medium hover:bg-secondary transition-colors flex items-center w-auto inline-flex">
                Request Investor Deck
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>
          <div className="md:w-1/3">
            <div className="bg-lightgray p-6 rounded-lg">
              <h4 className="font-bold text-primary mb-4">Funding Allocation</h4>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>GPU/Cloud Compute</span>
                    <span className="font-semibold">30%</span>
                  </div>
                  <div className="w-full bg-gray-300 rounded-full h-2">
                    <div className="bg-primary h-2 rounded-full" style={{ width: '30%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>R&D/Engineering</span>
                    <span className="font-semibold">30%</span>
                  </div>
                  <div className="w-full bg-gray-300 rounded-full h-2">
                    <div className="bg-primary h-2 rounded-full" style={{ width: '30%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Commercialization</span>
                    <span className="font-semibold">20%</span>
                  </div>
                  <div className="w-full bg-gray-300 rounded-full h-2">
                    <div className="bg-primary h-2 rounded-full" style={{ width: '20%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Regulatory</span>
                    <span className="font-semibold">10%</span>
                  </div>
                  <div className="w-full bg-gray-300 rounded-full h-2">
                    <div className="bg-primary h-2 rounded-full" style={{ width: '10%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Other</span>
                    <span className="font-semibold">10%</span>
                  </div>
                  <div className="w-full bg-gray-300 rounded-full h-2">
                    <div className="bg-primary h-2 rounded-full" style={{ width: '10%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-white p-8 rounded-xl shadow-md">
        <h3 className="text-2xl font-bold text-primary mb-6">Investment Highlights</h3>
        <div className="grid md:grid-cols-3 gap-8">
          <FeatureCard
            icon={<BarChart3 className="h-6 w-6 text-primary" />}
            title="First-to-Market Advantage"
            description="Senti has developed the world's first Large Biosignal Model, positioning us with a significant first-mover advantage in a rapidly growing market."
            variant="accent"
            className="transition-transform hover:scale-105"
          />
          <FeatureCard
            icon={<TrendingUp className="h-6 w-6 text-primary" />}
            title="Massive Market Potential"
            description="The global healthcare AI market is projected to exceed £250 billion by 2032, with biosignal analysis representing a critical, yet largely untapped, opportunity."
            variant="accent"
            className="transition-transform hover:scale-105"
          />
          <FeatureCard
            icon={<DollarSign className="h-6 w-6 text-primary" />}
            title="Proven Business Model"
            description="Our model-as-a-service approach delivers recurring revenue while creating high customer stickiness through regulatory barriers to switching."
            variant="accent"
            className="transition-transform hover:scale-105"
          />
        </div>
      </div>

      <div className="bg-white p-8 rounded-xl shadow-md">
        <h3 className="text-2xl font-bold text-primary mb-6">Strategic Roadmap</h3>
        <div className="relative">
          <div className="absolute left-4 md:left-1/2 h-full w-1 bg-lightgray"></div>
          
          <div className="relative z-10">
            <div className="flex flex-col md:flex-row md:items-center mb-12">
              <div className="md:w-1/2 mb-4 md:mb-0 md:pr-12 md:text-right">
                <h4 className="font-semibold text-primary mb-1">Today</h4>
                <p className="text-gray-700">Proof of Concept demonstrated</p>
              </div>
              <div className="flex items-center md:justify-center">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                  <div className="w-3 h-3 bg-white rounded-full"></div>
                </div>
              </div>
              <div className="md:w-1/2 md:pl-12 ml-10 md:ml-0">
                <p className="text-gray-700">
                  Initial LBM (GPT-1 scale) extracts biomarkers from signals and correlates with disease states.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row md:items-center mb-12">
              <div className="md:w-1/2 mb-4 md:mb-0 md:pr-12 md:text-right">
                <h4 className="font-semibold text-primary mb-1">Q2 2025</h4>
                <p className="text-gray-700">Clinical Validation</p>
              </div>
              <div className="flex items-center md:justify-center">
                <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                  <div className="w-3 h-3 bg-primary rounded-full"></div>
                </div>
              </div>
              <div className="md:w-1/2 md:pl-12 ml-10 md:ml-0">
                <p className="text-gray-700">
                  Publish clinical trial findings on respiratory application. Demonstrate biomarker separation 
                  from noise markers. Scale model to GPT-2 equivalent.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row md:items-center mb-12">
              <div className="md:w-1/2 mb-4 md:mb-0 md:pr-12 md:text-right">
                <h4 className="font-semibold text-primary mb-1">Q4 2025</h4>
                <p className="text-gray-700">Regulatory Approval & Scale</p>
              </div>
              <div className="flex items-center md:justify-center">
                <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                  <div className="w-3 h-3 bg-primary rounded-full"></div>
                </div>
              </div>
              <div className="md:w-1/2 md:pl-12 ml-10 md:ml-0">
                <p className="text-gray-700">
                  Secure FDA approval for respiratory product. Demonstrate system generalizability 
                  to other applications. Scale model to GPT-3 equivalent.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row md:items-center">
              <div className="md:w-1/2 mb-4 md:mb-0 md:pr-12 md:text-right">
                <h4 className="font-semibold text-primary mb-1">2026</h4>
                <p className="text-gray-700">Market Dominance</p>
              </div>
              <div className="flex items-center md:justify-center">
                <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                  <div className="w-3 h-3 bg-primary rounded-full"></div>
                </div>
              </div>
              <div className="md:w-1/2 md:pl-12 ml-10 md:ml-0">
                <p className="text-gray-700">
                  Position Senti's LBM as the industry standard in healthcare AI. Expand to multiple
                  clinical applications. Scale model to GPT-4 equivalent.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestmentOverview;